import React from "react";
//#region types
import { TodoCardProps } from "models/propTypes/todoPropTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { ImageMeta } from "models/propTypes/fileViewerPropTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
//endregion
//#region components
import TopTodoCard from "../../_partials/topTodoCard";
import {
  FileViewerContainer,
  GenericCommentsView,
  TitledContainerInner,
  EntityCardContent,
  useMountedState,
} from "components/common";
import {
  HighReceiptAmountFloatingMessage,
  ReceiptAlerts,
  ReceiptFooter,
  ReceiptTodoSelectionFormContainer,
  ReceiptUploadedBy,
  useReceiptDownload,
  useReceiptSnoozeAction,
} from "components/receipt";
import { useReceiptViewInfo } from "components/receipt";
import { useReceiptService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const ReceiptTodoToApprove: React.FC<TodoCardProps> = (props) => {
  const receiptService = useReceiptService();
  const receipt = props.item.receipt;
  const formRef = React.useRef<FormHandles>(null);

  const { getMounted } = useMountedState();
  const [loading, setLoading] = React.useState(false);
  const [updatedReceiptCategory, setUpdatedReceiptCategory] = React.useState<string>();
  const [updatedManualPayment, setUpdatedManualPayment] = React.useState<boolean>();

  const attachments: ImageMeta[] =
    (receipt &&
      receipt.attachments?.map((meta) => ({
        id: meta.id,
        contentType: meta.contentType,
        numberOfPages: 1,
        data: meta.data,
        loading: (meta as unknown as ImageMeta).loading,
      }))) ??
    [];

  const onDownloadClick = useReceiptDownload(receipt?.id ?? 0);
  const onViewInfoClick = useReceiptViewInfo(receipt!.id, formRef);
  const quickActions = [useReceiptSnoozeAction(receipt!.id, props.onActionSuccess)];

  const submitReceiptForm = (submitFunc?: (values: any) => Promise<any>) => {
    if (formRef.current) {
      return formRef.current.submit(submitFunc);
    }

    return (submitFunc && submitFunc({})) || new Promise<void>((resolve) => resolve());
  };

  const reloadTodo = (onSuccess?: (receipt: WintSuperkollAPIModel.Receipt) => void, onError?: () => void) => {
    if (receipt) {
      setLoading(true);
      receiptService
        .get(receipt.id)
        .then((result) => {
          onSuccess && onSuccess(result);
        })
        .catch(() => {
          onError && onError();
        })
        .finally(() => {
          if (getMounted()) setLoading(false);
          props.onActionSuccess();
        });
    }
  };

  const onReceiptCategoryChange = (item: string) => setUpdatedReceiptCategory(item);
  const onManualPaymentChange = (val: boolean) => setUpdatedManualPayment(val);

  const fileViewer = React.useMemo(
    () => (
      <FileViewerContainer
        key={receipt?.id}
        meta={attachments}
        parentId={receipt?.id ?? 0}
        onDownloadClick={onDownloadClick}
        loading={receipt === undefined}
        getFile={receipt ? receiptService.getAttachmentData.bind(null, receipt.id) : undefined}
      />
    ),
    [receipt?.id, receipt?.attachments]
  );

  const hasMissingPaymentDetails = updatedManualPayment ?? receipt?.hasMissingPaymentDetails;

  const getDescriptionHeadingTwo = () => {
    const arr: string[] = [];
    const category = updatedReceiptCategory ?? receipt?.categoryName ?? "";
    if (category) arr.push(category);
    if (receipt?.supplierName) arr.push(receipt.supplierName);
    return arr.join(", ");
  };

  return (
    <EntityCardContent
      topTodoCard={
        <TopTodoCard
          item={props.item}
          descriptionHeadingOne={LocalizationHelperInstance.getFormattedCurrency(
            receipt?.amount ?? 0,
            receipt?.currency
          )}
          descriptionHeadingTwo={getDescriptionHeadingTwo()}
          descriptionheadingThree={
            <ReceiptUploadedBy personId={receipt?.createdByPersonId} personName={receipt?.createdByPersonName} />
          }
          footerText={`${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.PurchaseDate)}: ${
            receipt?.dateTime ? LocalizationHelperInstance.getFormattedDate(receipt?.dateTime) : ""
          }`}
          onViewAllClick={onViewInfoClick}
          quickActions={quickActions}
        />
      }
      todoMainSection={
        <TitledContainerInner
          header={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.ConfirmTheInformation)}
          subHeader={LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Receipt.ConfirmTheInformationDescription
          )}
        >
          {receipt && (
            <ReceiptTodoSelectionFormContainer
              ref={formRef}
              receipt={receipt}
              fileViewer={fileViewer}
              receiptCategoryChangeHandler={onReceiptCategoryChange}
              receiptManualPaymentChangeHandler={onManualPaymentChange}
            />
          )}
        </TitledContainerInner>
      }
      infoViewer={fileViewer}
      footer={
        receipt ? (
          <ReceiptFooter
            receipt={receipt}
            receiptFormSubmitHandler={submitReceiptForm}
            reloadReceipt={reloadTodo}
            markAsDone={props.onActionSuccess}
            hideSave
            disableSuccessMessages
          />
        ) : (
          <></>
        )
      }
      loading={loading}
    >
      <GenericCommentsView parentId={receipt!.id} type={WintSuperkollAPIModel.GenericCommentType.Receipt} />
      <ReceiptAlerts hasMissingPaymentDetails={hasMissingPaymentDetails} />
      <HighReceiptAmountFloatingMessage
        amount={receipt?.amount}
        currency={receipt?.currency}
        receiptCategoryId={receipt?.receiptCategoryId}
        date={receipt?.dateTime ? new Date(receipt.dateTime) : undefined}
      />
    </EntityCardContent>
  );
};

export default ReceiptTodoToApprove;
